import { Fragment, useEffect, useState } from "react";
import { Checkbox, Modal, createStyles, Textarea, Space, Text, NumberInput, ActionIcon, Button, Divider, Group } from "@mantine/core";
import { IconX, IconPlus, IconEdit } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { alphabet } from "../../assets/constants";

const useStyles = createStyles((theme) => ({

    modal: {
        // zIndex: 99999999,

        '.mantine-Modal-title':{
            fontSize: '18px',
            fontWeight: '500',
        }
    },

    chosenText: {
        width: '58.58px',
    },

    customFlexGroup: {
        display: 'flex',
        alignItems: 'center',
    },

    submitButton: {
        minWidth: '70%',
    },

    optionTextArea: {
        flexGrow: 1,
    },

    checkBoxContainer: {
        // Same width as chosen text
        width: '58.58px',
        display: 'flex',
        justifyContent: 'center',
    },

    checkBox: {
        height: '24px'
    },

}));

const initialModalData = {
    questionType: 'Multiple choice',
    question: '',
    options: ['', ''],
    chosenOptionIndex: 0,
    response: '',
}

const exampleQuestions = {
    'Multiple choice': 'Did you eat breakfast this morning?',
    'Open ended': 'Describe your favorite meal.',
    'Number entry': 'What year were you born in?',
}

const getAnswerErrors = (values) => {
    if (values.length < 2) return "Please enter at least 2 answers";
    let errors = [];
    values.forEach((answer, index) => {
        if (answer.length < 1) {
            errors[index] = "Please enter an answer";
        } else if (values.filter((v) => v === answer).length > 1) {
            errors[index] = "Please enter a unique answer";
        } else {
            errors[index] = null;
        }
    });
    // Set first "Please enter a unique answer" error to null
    const firstUniqueErrorIndex = errors.findIndex((e) => e === "Please enter a unique answer");
    if (firstUniqueErrorIndex > -1) {
        errors[firstUniqueErrorIndex] = null;
    }
    return errors;
};

const validateSurveyAnswers = (values) => {
    const errors = getAnswerErrors(values);
    const numErrors = errors.filter((e) => e !== null).length;
    if (numErrors > 0) {
        return 'Error';
    }
};

const ResponseConditioningModal = ({
    surveyForm,
    conditionModalOpen,
    setConditionModalOpen,
    modalData,
    setModalData,
    }) => {

    const dataForModal = modalData ? modalData : initialModalData;

    const [editingModal,setEditingModal] = useState(false);

    const close = () => {
        setConditionModalOpen(false);
        setModalData(null);
    }

    const modalForm = useForm({
        initialValues: { ...dataForModal },
        validate: {
            question: (value) => value.length < 1 && 'Please enter a question',
            options: value => modalForm.values.questionType === 'Multiple choice' && validateSurveyAnswers(value),
            response: value => modalForm.values.questionType !== 'Multiple choice' && value.length < 1 && 'Please enter a response',
        },
    });

    useEffect(()=>{
        modalForm.setValues(dataForModal);
        const modalIsEditing = modalData ? true : false;
        setEditingModal(modalIsEditing);
    },[conditionModalOpen]);

    const { classes, cx } = useStyles();

    const removeSurveyItem = (fieldName, index) => {
        modalForm.removeListItem(fieldName, index)
    }

    const getInputAutoFocus = (fieldName, index) => {
        const values = modalForm.getInputProps(fieldName).value;
        const minLength = fieldName === 'options' ? 3 : 1;
        if (values.length < minLength) return false;
        const indexIsLast = index === values.length - 1;
        if (!indexIsLast) return false;
        const value = values[index];
        return value === '';
    }

    const highlightFormErrors = () => {
        const answerErrors = getAnswerErrors(modalForm.getInputProps('options').value);
        answerErrors.forEach((error, index) => {
            if (error !== null) modalForm.setFieldError(`options.${index}`, error);
        });
        if (modalForm.values.question.length === 0) {
            modalForm.setFieldError('question', 'Please enter a question');
        }
        if (modalForm.values.questionType !== 'Multiple choice' && modalForm.values.response.length === 0) {
            modalForm.setFieldError('response', 'Please enter a response');
        }
    }

    const submissionHandler = () => {
        if (!modalForm.isValid()) {
            highlightFormErrors();
            return;
        }
        const newConditioningQuestion = {
            questionType: modalForm.values.questionType,
            question: modalForm.values.question,
            options: modalForm.values.options,
            chosenOptionIndex: modalForm.values.chosenOptionIndex,
            response: modalForm.values.response,
        };
        if (editingModal) {
            const newQuestionsAndResponses = surveyForm.values.customQuestionsAndResponses.map((item) => {
                return item.question === modalData.question ? newConditioningQuestion : item;
            });
            surveyForm.setFieldValue('customQuestionsAndResponses', newQuestionsAndResponses);
        } else {
            const newQuestionsAndResponses = [...surveyForm.values.customQuestionsAndResponses, newConditioningQuestion];
            surveyForm.setFieldValue('customQuestionsAndResponses', newQuestionsAndResponses);
        }
        modalForm.reset();
        setConditionModalOpen(false);
    }

    const subtleButtonHandler = () => {
        if (editingModal) {
            // Remove the conditioning question
            const newConditioningQuestions = surveyForm.values.customQuestionsAndResponses.filter((item) => {
                return item.question !== modalData.question;
            });
            surveyForm.setFieldValue('customQuestionsAndResponses', newConditioningQuestions);
            setModalData(null);
            setConditionModalOpen(false);
        } else {
            modalForm.reset();
        }
    }

    return (
        <Modal
            className={classes.modal}
            opened={conditionModalOpen}
            onClose={close}
            centered
            padding='xl'
            size = '500px'
            closeOnClickOutside={true}
            radius='lg'
            title="Add a conditioning question"
        >
            <Text color='gray' size='sm'>
                Add a question and response to condition the responses.
                For example, if you want to run a survey on driving habits, 
                you can add a conditioning question asking if they own a car with an answer of "yes".
            </Text>
            <Space h='xl' />
            {/* <Text className={classes.titleText}>Question type</Text>
            <Space h={5} />
            <Select
                radius='md'
                size='md'
                data={['Multiple choice', 'Open ended', 'Number entry',]}
                defaultValue='United States'
                {...modalForm.getInputProps('questionType')}
            />
            <Space h='md' /> */}
            <Text className={classes.titleText}>Question</Text>
            <Space h={5} />
            <Textarea
                radius='md'
                placeholder={exampleQuestions[modalForm.values.questionType]}
                size='md'
                autosize
                {...modalForm.getInputProps('question')}
                maxLength='750'
            />
            <Space h='md' />
            {
                modalForm.values.questionType === 'Multiple choice' &&
                <>
                    <Group position='apart'>
                        <Text className={classes.titleText}>Options</Text>
                        <Text className={cx(classes.titleText, classes.chosenText)}>Chosen</Text>
                    </Group>
                    <Space h={5} />
                    {
                        modalForm.getInputProps(`options`).value.map((answer, index) => (
                            <Fragment key={`option-${index}`}>
                                <div className={classes.customFlexGroup}>
                                    <Textarea
                                        radius='md'
                                        className={classes.optionTextArea}
                                        placeholder={`Set option ${alphabet[index]}`}
                                        autosize
                                        size='md'
                                        maxLength='100'
                                        {...modalForm.getInputProps(`options.${index}`)}
                                        autoFocus={getInputAutoFocus('options', index)}
                                        onBlur={() => {
                                            if ((answer) === '' && modalForm.getInputProps(`options`).value.length > 2) {
                                                removeSurveyItem('options', index);
                                            }
                                        }}
                                        icon={<Text>{alphabet[index]}</Text>}
                                        rightSection={
                                            modalForm.getInputProps(`options`).value.length > 2 && (
                                                <ActionIcon
                                                    size={32}
                                                    radius="xl"
                                                    onMouseDown={() => { removeSurveyItem('options', index) }}
                                                >
                                                    <IconX stroke={1.5} size={20} />
                                                </ActionIcon>
                                            )
                                        }
                                    />
                                    <div className={classes.checkBoxContainer}>
                                        <Checkbox
                                            className={classes.checkBox}
                                            size='md'
                                            checked={modalForm.values.chosenOptionIndex === index}
                                            onChange={() => {
                                                modalForm.setFieldValue('chosenOptionIndex', index);
                                            }}
                                        />
                                    </div>
                                </div>
                                <Space h='xs' />
                            </Fragment>
                        ))
                    }
                    {
                        modalForm.getInputProps(`options`).value.length < 26 &&
                        (
                            <Button
                            variant='subtle'
                            leftIcon={<IconPlus size={20} stroke={1.5} />}
                            color='gray'
                            radius='md'
                            onClick={() => { modalForm.insertListItem('options', '') }}
                            style={{ fontWeight: '400' }}
                        >
                            Add an option
                        </Button>
                        )
                    }
                </>
            }
            {
                modalForm.values.questionType !== 'Multiple choice' &&
                <>
                    <Text className={classes.titleText}>Response</Text>
                    <Space h={5} />
                    {
                        modalForm.values.questionType === 'Open ended' &&
                        <Textarea
                            radius='md'
                            placeholder='Enter a response'
                            size='md'
                            autosize
                            {...modalForm.getInputProps('response')}
                            maxLength='750'
                        />
                    }
                    {
                        modalForm.values.questionType === 'Number entry' &&
                        <NumberInput
                            radius='md'
                            placeholder='Enter a number'
                            size='md'
                            hideControls
                            {...modalForm.getInputProps('response')}
                            styles={{ input: { width: '200px' } }}
                        />
                    }
                </>
            }
            <Space h='xl' />
            <Divider />
            <Space h='xl' />
            <Group position='apart' spacing='xs'>
                <Button
                    className={classes.submitButton}
                    size='lg'
                    radius='md'
                    onClick={submissionHandler}
                    leftIcon={editingModal ? <IconEdit stroke={1.5}/> : <IconPlus stroke={1.5}/>}
                >
                    {editingModal ? 'Update question' : 'Add question'} 
                </Button>
                <Button
                    variant = 'subtle'
                    radius='md'
                    color= {editingModal && 'red'}
                    onClick={subtleButtonHandler}
                >
                    {editingModal ? 'Remove' : 'Reset'}
                </Button>
            </Group>
            <Space h='xs' />
        </Modal>
    )
}

export default ResponseConditioningModal;